import type { Locale } from '@autho/i18n';
import { formatDateTime, isLocale } from '@autho/i18n';
import { ConfidenceBadge, Drawer } from '@autho/ui';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useProvenance } from '../lib/api.ts';
import { useT } from '../lib/preferences.tsx';
import { useTenant } from '../lib/tenant.tsx';
import { ErrorNote } from '../pages/common.tsx';
import { useProvenanceParam } from '../router.tsx';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2 border-b border-border py-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

/** Design Principles #1: provider, object id, observed at, confidence, derivation — for any record. */
export function ProvenanceDrawer() {
  const t = useT();
  const intl = useIntl();
  const locale = (isLocale(intl.locale) ? intl.locale : 'en') as Locale;
  const { tenantId } = useTenant();
  const [target, setTarget] = useProvenanceParam();
  const provenance = useProvenance(tenantId, target?.table ?? null, target?.id ?? null);
  const data = provenance.data;
  const providerKey = `provenance.provider.${data?.source.provider ?? ''}`;

  return (
    <Drawer
      open={Boolean(target)}
      onOpenChange={(open) => !open && setTarget(null)}
      title={t('provenance.title')}
    >
      {provenance.isPending && <p className="text-sm text-muted">{t('app.loading')}</p>}
      {/* The same sweep: this failure carries a status and often the server's own sentence, and
       *something went wrong* threw both away (F202). `ErrorNote` is the one place that decides. */}
      {provenance.isError && <ErrorNote error={provenance.error} />}
      {data && (
        <dl>
          <Row label={t('provenance.confidence')}>
            <ConfidenceBadge level={data.confidence} />
          </Row>
          <Row label={t('provenance.provider')}>
            {intl.messages[providerKey]
              ? intl.formatMessage({ id: providerKey })
              : data.source.provider}
          </Row>
          <Row label={t('provenance.object')}>
            <code className="text-xs">
              {data.source.objectType}/{data.source.objectId}
            </code>
          </Row>
          <Row label={t('provenance.observedAt')}>
            {formatDateTime(data.source.observedAt, locale)}
          </Row>
          <Row label={t('provenance.lastVerified')}>
            {data.lastVerifiedAt
              ? formatDateTime(data.lastVerifiedAt, locale)
              : t('common.unknown')}
          </Row>
          <Row label={t('provenance.override')}>
            {data.userOverride && Object.keys(data.userOverride).length > 0 ? (
              <ul className="flex flex-col gap-1">
                {Object.entries(data.userOverride).map(([field, value]) => (
                  <li key={field}>
                    <code className="text-xs">{field}</code>
                    <span className="text-muted">{' → '}</span>
                    <span>{typeof value === 'string' ? value : JSON.stringify(value)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-muted">{t('provenance.override.none')}</span>
            )}
          </Row>
          <Row label={t('provenance.derivation')}>
            <ul className="flex flex-col gap-0.5 text-xs text-muted">
              {data.observations.map((o) => (
                <li
                  key={o.id}
                  className={o.id === data.source.id ? 'font-medium text-text' : undefined}
                >
                  {formatDateTime(o.observedAt, locale)}
                </li>
              ))}
            </ul>
          </Row>
        </dl>
      )}
    </Drawer>
  );
}
